// frontend/src/AppExplorer.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import Graph from 'react-graph-vis';
import Modal from 'react-modal';
import './App.css';
import ConfigModal from './ConfigModal'; // Import the updated ConfigModal

// --- Collapsible List Component ---
// (Assuming CollapsibleList component exists as provided previously)
function CollapsibleList({ title, items, limit = 20, initiallyExpanded = false }) {
    const [isExpanded, setIsExpanded] = useState(initiallyExpanded);
     if (!items || items.length === 0) { return (<div className="collapsible-list empty"><h5>{title} (0)</h5></div>); }
    const toggleExpand = () => setIsExpanded(!isExpanded);
    const displayedItems = isExpanded ? items : items.slice(0, limit);
    return (
        <div className="collapsible-list">
            <h5 onClick={toggleExpand} style={{ cursor: 'pointer' }}>
                {title} ({items.length}) {items.length > limit && (isExpanded ? ' [-]' : ' [+]')}
            </h5>
            {isExpanded && (
                <>
                    <ul>{displayedItems.map((item, index) => (<li key={index}>{typeof item === 'string' ? item : JSON.stringify(item)}</li>))}</ul>
                    {items.length > limit && (<button onClick={toggleExpand} className="toggle-expand">Show Less</button>)}
                </>
            )}
            {!isExpanded && items.length > limit && (
                <button onClick={toggleExpand} className="toggle-expand">Show More ({items.length - limit} more)</button>
            )}
            {!isExpanded && items.length <= limit && displayedItems.length > 0 && (
                <ul>{displayedItems.map((item, index) => (<li key={index}>{typeof item === 'string' ? item : JSON.stringify(item)}</li>))}</ul>
            )}
        </div>
    );
}

// --- Modal Style ---
const customModalStyles = {
    content: { top: '50%', left: '50%', right: 'auto', bottom: 'auto', marginRight: '-50%', transform: 'translate(-50%, -50%)', maxWidth: '80%', minWidth: '400px', maxHeight: '85vh', overflow: 'auto', padding: '25px', border: '1px solid #ccc', borderRadius: '8px', boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)', backgroundColor: '#fff', zIndex: 1001 },
    overlay: { backgroundColor: 'rgba(0, 0, 0, 0.6)', zIndex: 1000 },
};
// Modal.setAppElement('#root'); // Assuming this is done in App.js

// --- Component Definition ---
function AppExplorer({
    // Props from App.js
    isAuthenticated, directories,
    handleLogout,
    userRole, setUserRole,
    initialQuery, setInitialQuery,
    // Explorer Config values passed from App.js
    maxLoops, analyzeResponse, verifyCheckin, useRAG,
    // Handler to save Explorer Config in App.js
    handleSaveConfig, // Function from App.js to update its state
    // Other Props
    conversationList, selectedLogId, setSelectedLogId,
    fetchConversations,
    isLoading, setIsLoading, error, setError
}) {

    // --- State Variables ---
    const [uniqueId, setUniqueId] = useState(null);
    const [conversationName, setConversationName] = useState(null);
    const [loopCount, setLoopCount] = useState(0);
    const [isComplete, setIsComplete] = useState(false);
    const [completionReason, setCompletionReason] = useState('');
    const [memoryInfo, setMemoryInfo] = useState([]);
    const [selectedStatements, setSelectedStatements] = useState([]);
    const [currentNodes, setCurrentNodes] = useState([]);
    const [searchedTerms, setSearchedTerms] = useState([]);
    const [finalResponse, setFinalResponse] = useState('');
    const [ragContext, setRagContext] = useState([]);
    const [displayDataHistory, setDisplayDataHistory] = useState([]);
    const [activeStepIndex, setActiveStepIndex] = useState(-1);
    const [isAutoExploring, setIsAutoExploring] = useState(false);
    const [isViewingLog, setIsViewingLog] = useState(false);
    const [isAutoPlayingGraph, setIsAutoPlayingGraph] = useState(false);
    const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
    const [isArticleModalOpen, setIsArticleModalOpen] = useState(false);
    const [selectedNodeInfoForModal, setSelectedNodeInfoForModal] = useState(null);
    const [modalContent, setModalContent] = useState(null);
    const [isModalLoading, setIsModalLoading] = useState(false);
    // Data Source state for *current selection* (controlled here)
    const [filterType, setFilterType] = useState('directory'); // 'directory' or 'collection'
    const [selectedDirectory, setSelectedDirectory] = useState('');
    const [selectedCollection, setSelectedCollection] = useState('');
    // **NEW State**: Stores the source info of the *loaded* log
    const [loadedDataSourceInfo, setLoadedDataSourceInfo] = useState(null); // { type: 'directory'/'collection', name: '...' }

    const autoExploreTimeoutRef = useRef(null);
    const autoPlayIntervalRef = useRef(null);

    // --- Cleanup Effects ---
    useEffect(() => () => { if (autoExploreTimeoutRef.current) clearTimeout(autoExploreTimeoutRef.current); }, []);
    useEffect(() => () => { if (autoPlayIntervalRef.current) clearInterval(autoPlayIntervalRef.current); }, []);

    // --- Config Modal Handlers ---
    const openConfigModal = () => setIsConfigModalOpen(true);
    const closeConfigModal = () => setIsConfigModalOpen(false);
    const handleSaveDataSource = useCallback((newSource) => {
        console.log("Saving Data Source Selection:", newSource);
        const oldSource = filterType === 'directory' ? selectedDirectory : selectedCollection;
        const newSourceVal = newSource.filterType === 'directory' ? newSource.selectedDirectory : newSource.selectedCollection;

        // Only clear results if the actual source value changed
        if (newSource.filterType !== filterType || newSourceVal !== oldSource) {
            console.log("Data source changed, clearing results.");
            handleClear(false); // Keep inputs, clear results & loaded log info
        } else {
             console.log("Data source type/value unchanged, not clearing results.");
        }

        setFilterType(newSource.filterType);
        setSelectedDirectory(newSource.selectedDirectory);
        setSelectedCollection(newSource.selectedCollection);

    }, [filterType, selectedDirectory, selectedCollection]); // Added handleClear dependency

    // --- Article/Statement Modal Logic ---
    const closeArticleModal = () => { setIsArticleModalOpen(false); setModalContent(null); setSelectedNodeInfoForModal(null); setIsModalLoading(false); };
    const handleModalBackToList = () => { setModalContent(null); setIsModalLoading(false); };
    const fetchArticleDetailsForModal = useCallback(async (articleId) => {
        if (!articleId || articleId === 'N/A') { setModalContent({ error: `Invalid Article ID: ${articleId}` }); return; }
        console.log(`Fetching details for Article ID: ${articleId}`);
        setIsModalLoading(true); setModalContent(null);
        try {
            const response = await fetch(`/api/article_details/${articleId}`, { credentials: 'include' });
            const data = await response.json();
            if (!response.ok) {
                 if (response.status === 401) { console.error("Article details unauthorized. Logging out."); handleLogout(false); throw new Error("Session expired or invalid. Please log in again."); }
                throw new Error(data.error || `HTTP error ${response.status}`);
            }
            setModalContent(data);
        } catch (err) {
            console.error("Error fetching article details:", err);
            setModalContent({ error: `Failed to load details for Article ID ${articleId}: ${err.message}` });
        } finally { setIsModalLoading(false); }
    }, [handleLogout]);

    // --- handleClear function ---
    const handleClear = useCallback((clearInputsAndSelection = true) => {
        console.log(`handleClear called with clearInputsAndSelection=${clearInputsAndSelection}`);
        setIsAutoExploring(false); setIsAutoPlayingGraph(false);
        if (autoExploreTimeoutRef.current) clearTimeout(autoExploreTimeoutRef.current); autoExploreTimeoutRef.current = null;
        if (autoPlayIntervalRef.current) clearInterval(autoPlayIntervalRef.current); autoPlayIntervalRef.current = null;
        setIsComplete(false); setCompletionReason(''); setLoopCount(0);
        setMemoryInfo([]); setSelectedStatements([]); setCurrentNodes([]);
        setSearchedTerms([]); setUniqueId(null); setConversationName(null);
        setDisplayDataHistory([]); setActiveStepIndex(-1); setError(null);
        setIsLoading(false); setIsViewingLog(false); setFinalResponse('');
        setRagContext([]); setSelectedLogId('');
        setLoadedDataSourceInfo(null); // **Crucial**: Clear loaded data source info

        if (clearInputsAndSelection) {
             setInitialQuery(''); setUserRole('');
             setFilterType('directory'); // Reset filter type
             setSelectedDirectory('');   // Reset directory selection
             setSelectedCollection(''); // Reset collection selection
             console.log("Cleared all inputs, results, and filter selection.");
        } else {
             // Keep inputs, but still clear loaded source info if not clearing selection
             console.log("Cleared results, keeping inputs and filter selection.");
        }
        closeArticleModal();
     }, [setError, setIsLoading, setInitialQuery, setUserRole, setSelectedLogId]); // Dependencies remain the same


    // --- API Call Function (processRequest) ---
    const processRequest = useCallback(async (isInitial = false) => {
        if (isAutoPlayingGraph) { setIsAutoPlayingGraph(false); clearInterval(autoPlayIntervalRef.current); autoPlayIntervalRef.current = null;}
        setIsLoading(true); setError(null);
        if (isInitial) {
            setIsViewingLog(false);
            setLoadedDataSourceInfo(null); // ** Ensure loaded info is cleared when starting NEW request **
        }

        const filterValue = filterType === 'directory' ? selectedDirectory : selectedCollection;
        if (!filterValue) {
            setError(`Please select a data source (${filterType}) via the Config button.`);
            setIsLoading(false);
            return;
        }

        const currentUniqueId = isInitial ? null : uniqueId;
        const currentName = isInitial ? null : conversationName;

        const payload = {
            filterType, filterValue, initialQuery, userRole, maxLoops,
            analyzeResponse, useRAG, loopCount: isInitial ? 0 : loopCount,
            memoryInfo: isInitial ? [] : memoryInfo,
            selectedStatements: isInitial ? [] : selectedStatements,
            currentNodes: isInitial ? [] : currentNodes,
            searchedTerms: isInitial ? [] : searchedTerms,
            ragContext: isInitial ? [] : ragContext,
            uniqueId: currentUniqueId, conversationName: currentName,
        };
        console.log("Sending process request:", payload);
         try {
            const response = await fetch('/api/process', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload), credentials: 'include' });
            const responseData = await response.json();
            console.log("Received process response:", responseData);
             if (!response.ok) {
                setIsAutoExploring(false);
                 if (response.status === 401) { console.error("Process request unauthorized. Logging out."); handleLogout(false); throw new Error("Session expired or invalid. Please log in again."); }
                throw new Error(responseData.error || `HTTP error! status: ${response.status}`);
            }

            setUniqueId(responseData.uniqueId); setConversationName(responseData.conversationName);
            setLoopCount(responseData.loopCount); setMemoryInfo(responseData.memoryInfo);
            setSelectedStatements(responseData.selectedStatements); setCurrentNodes(responseData.currentNodes);
            setSearchedTerms(responseData.searchedTerms); setIsComplete(responseData.isComplete);
            setCompletionReason(responseData.completionReason || '');
            setFinalResponse(responseData.displayData?.stepFinalResponse || finalResponse);
            setRagContext(responseData.ragContext || []);
             const newDisplayData = responseData.displayData;
            if (newDisplayData) {
                 const displayStep = { ...newDisplayData, stepRagContext: newDisplayData.stepRagContext || [] };
                const updatedHistory = isInitial ? [displayStep] : [...displayDataHistory, displayStep];
                setDisplayDataHistory(updatedHistory); setActiveStepIndex(updatedHistory.length - 1);
            } else { console.warn("Backend response missing displayData!"); }

             const backendSuggestsContinue = !responseData.isComplete && responseData.currentNodes?.length > 0;
             if (isAutoExploring && backendSuggestsContinue) {
                 console.log("Auto-Explore: Backend suggests continuing. Scheduling next step.");
                 autoExploreTimeoutRef.current = setTimeout(() => processRequest(false), 500);
             } else if (isAutoExploring) {
                 setIsAutoExploring(false);
                 if (!backendSuggestsContinue) { console.log(`Auto-Explore finished: Backend suggested stopping or process complete. Reason: ${responseData.completionReason || 'No further nodes suggested.'}`); }
                 else { console.log("Auto-Explore finished."); }
             }
             if (isInitial || (responseData.isComplete && responseData.conversationName)) {
                 console.log("Scheduling conversation list refresh.");
                 setTimeout(fetchConversations, 1500);
            }
         } catch (e) {
            console.error("Error processing request:", e); setError(`Processing failed: ${e.message}`);
            setIsAutoExploring(false); if (autoExploreTimeoutRef.current) clearTimeout(autoExploreTimeoutRef.current); autoExploreTimeoutRef.current = null;
        } finally { setIsLoading(false); }
    }, [
        initialQuery, userRole, maxLoops, analyzeResponse, useRAG, // Props from App.js
        loopCount, memoryInfo, selectedStatements, currentNodes, searchedTerms, ragContext, uniqueId,
        conversationName, finalResponse, displayDataHistory, isAutoExploring,
        fetchConversations, handleLogout, setIsLoading, setError, isAutoPlayingGraph,
        filterType, selectedDirectory, selectedCollection // AppExplorer state for data source
    ]);


    // --- Load Log Handler ---
    const handleLoadLog = useCallback(async () => {
         if (!selectedLogId) { setError("Please select a Chat to load."); return; }
         console.log(`Attempting to load log: ${selectedLogId}`);
         setIsLoading(true); setError(null);
         // Don't clear inputs here, only results and internal state
         setIsAutoExploring(false); setIsAutoPlayingGraph(false);
         if (autoExploreTimeoutRef.current) clearTimeout(autoExploreTimeoutRef.current); autoExploreTimeoutRef.current = null;
         if (autoPlayIntervalRef.current) clearInterval(autoPlayIntervalRef.current); autoPlayIntervalRef.current = null;
         setIsComplete(false); setCompletionReason(''); setLoopCount(0);
         setMemoryInfo([]); setSelectedStatements([]); setCurrentNodes([]);
         setSearchedTerms([]); setUniqueId(null); setConversationName(null);
         setDisplayDataHistory([]); setActiveStepIndex(-1); setFinalResponse('');
         setRagContext([]);
         setLoadedDataSourceInfo(null); // Clear previous loaded info first
         setIsViewingLog(false); // Ensure viewing mode is off initially

         try {
            const response = await fetch(`/api/load_log/${selectedLogId}`, { credentials: 'include' });
            const responseData = await response.json();
            if (!response.ok) {
                 if (response.status === 401) { console.error("Load log unauthorized. Logging out."); handleLogout(false); throw new Error("Session expired or invalid. Please log in again."); }
                throw new Error(responseData.error || `HTTP error! status: ${response.status}`);
            }
             console.log("Loaded log data:", responseData);

             // **Critical Update Section**
             const loadedDataSelect = responseData.data_select;
             const loadedName = responseData.selectedDirectory;
             const loadedFilterType = loadedDataSelect === 1 ? 'collection' : 'directory';

             console.log(`Log loaded: Type='${loadedFilterType}', Name='${loadedName}', data_select='${loadedDataSelect}'`);

             // 1. Set the state reflecting the *loaded log's* source (for display)
             // THIS IS THE CRUCIAL STATE FOR THE DISPLAY FUNCTION
             setLoadedDataSourceInfo({ type: loadedFilterType, name: loadedName });

             // 2. *Also* update the *current selection* state to match the loaded log.
             //    This ensures the UI (dropdowns via ConfigModal) and subsequent graph key reflect the loaded context.
             setFilterType(loadedFilterType);
             if (loadedFilterType === 'directory') {
                 setSelectedDirectory(loadedName || '');
                 setSelectedCollection('');
             } else { // loadedFilterType === 'collection'
                 setSelectedCollection(loadedName || '');
                 setSelectedDirectory('');
             }
             // --- End Critical Update Section ---

             // Set the rest of the state from the loaded data
             setUniqueId(responseData.uniqueId);
             setInitialQuery(responseData.initialQuery); // Update inputs to match loaded log
             setUserRole(responseData.userRole);         // Update inputs to match loaded log
             setIsComplete(responseData.isComplete);
             setCompletionReason(responseData.completionReason || '');
             setLoopCount(responseData.loopCount);
             setMemoryInfo(responseData.memoryInfo || []);
             setSelectedStatements(responseData.selectedStatements || []);
             setCurrentNodes(responseData.currentNodes || []);
             setSearchedTerms(responseData.searchedTerms || []);
             setFinalResponse(responseData.finalResponse || '');
             setConversationName(responseData.conversationName);
             setRagContext(responseData.ragContext || []);
             const historyWithRag = (responseData.displayDataHistory || []).map(step => ({
                ...step,
                stepRagContext: Array.isArray(step.stepRagContext) ? step.stepRagContext : [],
             }));
             setDisplayDataHistory(historyWithRag);
             setActiveStepIndex((historyWithRag?.length || 0) - 1);

             // **Set viewing log mode LAST, after all state is updated**
             setIsViewingLog(true);

             console.log("Log loaded successfully, all relevant state updated.");

         } catch (e) {
             console.error("Error loading log:", e);
             setError(`Failed to load log ${selectedLogId}: ${e.message}`);
             handleClear(true); // Clear everything on error
         } finally {
             setIsLoading(false);
         }
    // Note: handleClear is used internally, not a direct dependency changing load behavior
    }, [selectedLogId, handleLogout, setIsLoading, setError, setInitialQuery, setUserRole, setSelectedLogId]);


    // --- Event Handlers ---
    const handleSubmit = (e) => {
        e.preventDefault();
        if (isLoading || isAutoExploring) return;
        const filterValue = filterType === 'directory' ? selectedDirectory : selectedCollection;
        if (!filterValue) { setError(`Please select a data source (${filterType}) via the Config button.`); return; }
        if (!initialQuery) { setError(`Please enter a query.`); return; }
        if (!userRole) { setError(`Please enter an expert role.`); return; }

        // Explicitly turn off viewing mode and clear loaded info when starting NEW query
        setIsViewingLog(false);
        setLoadedDataSourceInfo(null);
        // handleClear(false); // Clear results, keep inputs/filter
        // Resetting state for a new query sequence
        setIsComplete(false); setCompletionReason(''); setLoopCount(0);
        setMemoryInfo([]); setSelectedStatements([]); setCurrentNodes([]);
        setSearchedTerms([]); setUniqueId(null); setConversationName(null);
        setDisplayDataHistory([]); setActiveStepIndex(-1); setFinalResponse('');
        setRagContext([]);
        // Keep: initialQuery, userRole, filterType, selectedDirectory/Collection

        processRequest(true); // Start a new request
    };
    const handleExploreNext = () => {
        if (isLoading || isAutoExploring || isViewingLog) return; // Don't explore while viewing log
        if (!isComplete && currentNodes.length > 0) {
            processRequest(false); // Continue existing request
        } else if (!isComplete && currentNodes.length === 0 && loopCount > 0) {
            setError("Process finished: LLM suggested no further nodes.");
             setIsComplete(true); setCompletionReason("LLM suggested no further nodes.");
        }
     };
    const handleToggleAutoExplore = () => {
        if (isAutoExploring) {
            setIsAutoExploring(false); if (autoExploreTimeoutRef.current) clearTimeout(autoExploreTimeoutRef.current);
            console.log("Auto-Explore stopped by user."); setIsLoading(false);
        } else {
             if (isComplete || isViewingLog || isLoading) return; // Don't start if viewing log or complete
             const filterValue = filterType === 'directory' ? selectedDirectory : selectedCollection;
             if (!filterValue) { setError(`Please select a data source (${filterType}) via the Config button.`); return; }
             if (!initialQuery) { setError(`Please enter a query.`); return; }
             if (!userRole) { setError(`Please enter an expert role.`); return; }

             if (loopCount === 0 || currentNodes.length > 0) {
                 setIsViewingLog(false); // Ensure viewing mode is off
                 setLoadedDataSourceInfo(null); // Clear any loaded log info
                 setIsAutoExploring(true); console.log("Auto-Explore started.");
                 if (loopCount === 0) {
                    // Reset state for a new exploration sequence
                    setIsComplete(false); setCompletionReason(''); setLoopCount(0);
                    setMemoryInfo([]); setSelectedStatements([]); setCurrentNodes([]);
                    setSearchedTerms([]); setUniqueId(null); setConversationName(null);
                    setDisplayDataHistory([]); setActiveStepIndex(-1); setFinalResponse('');
                    setRagContext([]);
                    setTimeout(() => processRequest(true), 50); // Start initial request
                 } else {
                     processRequest(false); // Continue from current state
                 }
             } else { setError("Cannot start Auto-Explore: No further nodes suggested by the last step."); }
        }
     };

    // --- Step Navigation Handlers ---
    const handleViewStep = (index) => { if (isAutoPlayingGraph) { setIsAutoPlayingGraph(false); clearInterval(autoPlayIntervalRef.current); } setActiveStepIndex(index); };
    const handlePreviousStep = () => { if (isLoading || isAutoPlayingGraph) return; setActiveStepIndex(prev => Math.max(0, prev - 1)); };
    const handleNextStep = () => { if (isLoading || isAutoPlayingGraph) return; setActiveStepIndex(prev => Math.min(displayDataHistory.length - 1, prev + 1)); };

    // --- Graph Auto-Play Handler ---
    const handleToggleAutoPlayGraph = () => {
         if (isAutoPlayingGraph) {
            setIsAutoPlayingGraph(false); clearInterval(autoPlayIntervalRef.current); autoPlayIntervalRef.current = null;
        } else {
            if (displayDataHistory.length <= 1 || isLoading) return;
            setIsAutoPlayingGraph(true);
            let startIndex = activeStepIndex;
            if (startIndex < 0 || startIndex >= displayDataHistory.length -1) { startIndex = 0; }
            setActiveStepIndex(startIndex);
            autoPlayIntervalRef.current = setInterval(() => {
                setActiveStepIndex(prev => {
                    const nextIndex = prev + 1;
                    if (nextIndex >= displayDataHistory.length || isLoading) {
                        setIsAutoPlayingGraph(false); clearInterval(autoPlayIntervalRef.current); autoPlayIntervalRef.current = null;
                        return prev;
                    } return nextIndex;
                });
            }, 3000);
        }
    };

    // --- Graph Visualization Helpers ---
    const graphOptions = { /* ... options remain the same ... */
        layout: { hierarchical: false },
        physics: { solver: 'forceAtlas2Based', forceAtlas2Based: { gravitationalConstant: -55, centralGravity: 0.015, springLength: 110, springConstant: 0.08, damping: 0.5, avoidOverlap: 0.6 }, stabilization: { enabled: true, iterations: 150, updateInterval: 25 } },
        edges: { color: { color: "#848484", highlight: '#ff0000', hover: '#d3d3d3' }, arrows: { to: { enabled: true, scaleFactor: 0.6 } }, font: { size: 9, color: '#a0a0a0', strokeWidth: 0 }, smooth: { enabled: true, type: "continuous", roundness: 0.5 }, width: 1, hoverWidth: 1.5 },
        nodes: { shape: 'dot', size: 12, borderWidth: 1.5, font: { size: 11, color: '#333' }, color: { border: '#666', background: '#D2E5FF', highlight: { border: '#2B7CE9', background: '#87CEFA' }, hover: { border: '#2B7CE9', background: '#ADD8E6' } } },
        interaction: { tooltipDelay: 150, hover: true, navigationButtons: true, keyboard: true, zoomView: true, dragView: true },
        height: '100%', width: '100%'
    };
    const getGraphDataForStep = useCallback((stepIndex) => {
        if (stepIndex < 0 || stepIndex >= displayDataHistory.length) return { nodes: [], edges: [], nodeLabelMap: {} };
        const stepData = displayDataHistory[stepIndex];
        if (!stepData || !stepData.graphStructure) {
            console.warn(`No valid graph data found for step index ${stepIndex}`);
            return { nodes: [], edges: [], nodeLabelMap: {} };
        }
        const structure = stepData.graphStructure || { nodes: [], edges: [] };
        const highlights = stepData.graphHighlights || { currentNodes: [], connectedNodes: [] };
        // console.log(`Step ${stepIndex} structure:`, structure); // Debug: Check data source

        const nodes = (structure.nodes || []).map(node => {
             if (!node || typeof node.id === 'undefined') return null;
             let extraStyle = {};
             if ((highlights.currentNodes || []).includes(node.id)) extraStyle = { color: { background: '#FFBDBD', border: '#FF0000' }, size: 20, shape: 'star', font: { color: '#A52A2A', size: 13, weight: 'bold' }, borderWidth: 2 };
             else if ((highlights.connectedNodes || []).includes(node.id)) extraStyle = { color: { background: '#FFEBCD', border: '#FFA500' }, size: 16, shape: 'diamond', borderWidth: 1.5, font: { size: 12 } };
             return { ...node, label: String(node.label ?? ''), ...extraStyle };
         }).filter(Boolean);

        const edges = (structure.edges || []).map(edge => {
             if (!edge || typeof edge.from === 'undefined' || typeof edge.to === 'undefined') return null;
             const isFromCurrent = (highlights.currentNodes || []).includes(edge.from);
             const isToCurrent = (highlights.currentNodes || []).includes(edge.to);
             let edgeStyle = {};
             if (isFromCurrent || isToCurrent) edgeStyle = { color: { color: '#FF6347', highlight: '#FF0000' }, width: 2.5 };
             return { ...edge, articleId: edge.articleId, title: edge.title, ...edgeStyle };
         }).filter(Boolean);

         const nodeLabelMap = nodes.reduce((acc, node) => { if (node) acc[node.id] = node.label; return acc; }, {});
        return { nodes, edges, nodeLabelMap };
    }, [displayDataHistory]);
    const activeGraphData = getGraphDataForStep(activeStepIndex);
    const canGoPrevious = activeStepIndex > 0;
    const canGoNext = activeStepIndex < displayDataHistory.length - 1;


    // --- Graph Click Handler ---
     const handleGraphClick = useCallback((event) => {
        const { nodes: clickedNodeIds } = event;
        if (clickedNodeIds.length > 0) {
            const clickedNodeId = clickedNodeIds[0];
            const currentGraphData = getGraphDataForStep(activeStepIndex);
            const nodeLabel = currentGraphData?.nodeLabelMap?.[clickedNodeId] ?? 'Unknown Node';
            console.log(`Node clicked: ID=${clickedNodeId}, Label='${nodeLabel}'`);
            const connectedEdges = (currentGraphData?.edges || []).filter(edge =>
                 (edge.from === clickedNodeId || edge.to === clickedNodeId) &&
                 edge.articleId && edge.articleId !== 'N/A'
            );
            const statements = connectedEdges.map(edge => ({
                 statement: edge.title || `Edge from ${edge.from} to ${edge.to}`,
                 articleId: edge.articleId
            }));
             console.log(`Formatted statements for modal:`, statements);
            setSelectedNodeInfoForModal({ nodeId: clickedNodeId, nodeLabel: nodeLabel, connectedStatements: statements });
            setModalContent(null); setIsModalLoading(false); setIsArticleModalOpen(true);
        }
    }, [activeStepIndex, getGraphDataForStep]); // Dependencies


    // --- Determine current data source display string ---
    const currentDataSourceDisplay = () => {
        // Add logging here to see the state values during render
        // console.log(`Displaying source: isViewingLog=${isViewingLog}, loadedDataSourceInfo=`, loadedDataSourceInfo, `filterType=${filterType}, selectedDir=${selectedDirectory}, selectedColl=${selectedCollection}`);

        if (isViewingLog && loadedDataSourceInfo) {
            const typeLabel = loadedDataSourceInfo.type.charAt(0).toUpperCase() + loadedDataSourceInfo.type.slice(1);
            return `${typeLabel}: '${loadedDataSourceInfo.name}' (Loaded Log)`;
        } else if (filterType === 'directory' && selectedDirectory) {
            return `Directory: '${selectedDirectory}'`;
        } else if (filterType === 'collection' && selectedCollection) {
            return `Collection: '${selectedCollection}'`;
        } else {
            return 'No Data Source Selected';
        }
    };


    // --- JSX Return ---
    return (
        <>
            <div className="main-layout">
                <div className="left-column">
                    {/* --- Control Panel Form --- */}
                    <form onSubmit={handleSubmit} className="control-panel">
                        {/* Display Current Data Source - Uses updated logic */}
                        <div className="form-section" style={{ gridColumn: 'span 2', marginBottom: '15px', paddingBottom: '10px', borderBottom: '1px dashed #dee2e6' }}>
                           <label style={{marginBottom: '3px'}}>Current Data Source:</label>
                           <span style={{ fontStyle: (filterType === 'directory' && selectedDirectory) || (filterType === 'collection' && selectedCollection) || (isViewingLog && loadedDataSourceInfo) ? 'normal' : 'italic', color: (filterType === 'directory' && selectedDirectory) || (filterType === 'collection' && selectedCollection) || (isViewingLog && loadedDataSourceInfo) ? '#212529' : '#6c757d', fontWeight: '500' }}>
                                {currentDataSourceDisplay()} {/* Renders based on state */}
                            </span>
                            {/* Button to open ConfigModal */}
                            <button type="button" onClick={openConfigModal} style={{ marginLeft: '10px', fontSize: '0.8em', padding: '2px 6px' }} disabled={isLoading || isAutoExploring} title="Change Data Source or Settings"> Change... </button>
                        </div>
                        {/* Role, Config Button, Query */}
                        <div className="form-section"> <label htmlFor="userRole">Expert Role:</label> <input id="userRole" type="text" value={userRole} onChange={(e) => setUserRole(e.target.value)} placeholder="e.g., Financial Analyst" required disabled={isLoading || isAutoExploring || isViewingLog} /> </div>
                        <div className="form-section config-button-section"> <button type="button" className='config-button' onClick={openConfigModal} disabled={isLoading || isAutoExploring}> Config ⚙ </button> </div>
                        <div className="form-section query-input"> <label htmlFor="initialQuery">Your Query:</label> <textarea id="initialQuery" value={initialQuery} onChange={(e) => setInitialQuery(e.target.value)} placeholder="Enter your research question..." required rows={3} disabled={isLoading || isAutoExploring || isViewingLog} /> </div>
                        {/* Buttons */}
                        <div className="form-section button-group">
                            <button type="submit" className='primary' disabled={ isLoading || isAutoExploring || !( (filterType === 'directory' && selectedDirectory) || (filterType === 'collection' && selectedCollection) ) || !initialQuery || !userRole }> {isLoading && loopCount === 0 && !isViewingLog ? 'Processing...' : 'Start New Query'} </button>
                            <button type="button" className={`secondary auto-explore ${isAutoExploring ? 'active' : ''}`} onClick={handleToggleAutoExplore} disabled={isLoading || isComplete || isViewingLog || !( (filterType === 'directory' && selectedDirectory) || (filterType === 'collection' && selectedCollection) ) || !initialQuery || !userRole}> {isAutoExploring ? (isLoading ? 'Exploring...' : 'Stop Auto-Explore') : 'Auto Explore'} </button>
                            <button type="button" className='secondary' onClick={handleExploreNext} disabled={isLoading || isAutoExploring || isComplete || isViewingLog || currentNodes.length === 0}> {isLoading && loopCount > 0 && !isViewingLog && !isAutoExploring ? 'Exploring...' : 'Explore Next'} </button>
                            <button type="button" className='clear' onClick={() => handleClear(true)} disabled={isLoading}> Clear / Reset All </button>
                        </div>
                        {/* Load Log Section */}
                        <div className="form-section load-log-section">
                           <label htmlFor="logIdSelect">Load Previous Chat:</label>
                           <div className="load-log-controls">
                             <select id="logIdSelect" value={selectedLogId} onChange={(e) => setSelectedLogId(e.target.value)} disabled={isLoading || isAutoExploring}>
                               <option value="" disabled>-- Select a Chat --</option>
                               {conversationList.map(convo => (<option key={convo.id} value={convo.id}>{convo.name} ({convo.id.substring(0, 8)}...)</option>))}
                             </select>
                             <button type="button" className='load-chat' onClick={handleLoadLog} disabled={isLoading || isAutoExploring || !selectedLogId}> Load Chat </button>
                           </div>
                        </div>
                    </form>

                    {/* Loading & Error Display */}
                    {isLoading && <div className="loading">Loading... {isViewingLog ? 'Chat Data' : (isAutoExploring ? `Auto-Exploring Step ${loopCount}`: `Step ${loopCount}`)}</div>}
                    {error && <div className="error">{error}</div>}

                    {/* Results Area */}
                    {displayDataHistory.length > 0 && (
                         <div className="results-container">
                             {/* Updated title to be more generic */}
                             <h3> Exploration Process {isViewingLog ? `(Loaded: ${conversationName || (uniqueId ? uniqueId.substring(0,8)+'...' : 'Chat')})` : ''} ({displayDataHistory.length} Steps) </h3>
                             {/* Display loaded source info explicitly when viewing log */}
                             {isViewingLog && loadedDataSourceInfo && <p style={{fontSize: '0.9em', color: '#555', marginTop: '-10px', marginBottom: '15px'}}>Source: {loadedDataSourceInfo.type === 'collection' ? 'Collection' : 'Directory'} - '{loadedDataSourceInfo.name}'</p>}
                             {isComplete && completionReason && <p className="completion-info"><strong>Status: Complete</strong> ({completionReason})</p>}
                             {isComplete && finalResponse && ( <> <h4 style={{ color: 'green', marginTop: '15px' }}>Final Synthesized Response:</h4> <pre className="final-response">{finalResponse}</pre> </> )}
                             {useRAG && Array.isArray(ragContext) && ragContext.length > 0 && ( <> <h4 style={{ color: 'purple', marginTop: '15px' }}>RAG Context Snippets:</h4> <CollapsibleList title="Retrieved Document Snippets" items={ragContext} limit={3} initiallyExpanded={false} /> </> )}
                             {/* Step Details */}
                             {activeStepIndex >= 0 && activeStepIndex < displayDataHistory.length ? (
                                (() => {
                                    const activeStepData = displayDataHistory[activeStepIndex];
                                    if (!activeStepData) return null;
                                    return (
                                        <div className="active-chat-step">
                                            <h4> Chat Details - Step {activeStepData.stepLoopCount != null ? activeStepData.stepLoopCount + 1 : activeStepIndex + 1} </h4>
                                            {activeStepData.stepCurrentNodes?.length >= 0 && <CollapsibleList title="Nodes Explored" items={activeStepData.stepCurrentNodes || []} limit={10} initiallyExpanded={false} />}
                                            {activeStepData.stepNewlySelectedStatements?.length >= 0 && <CollapsibleList title="Statements Added" items={activeStepData.stepNewlySelectedStatements || []} limit={10} initiallyExpanded={false} />}
                                            {activeStepData.stepConnectedStatements?.length >= 0 && <CollapsibleList title="Connected Statements" items={activeStepData.stepConnectedStatements || []} limit={15} initiallyExpanded={false} />}
                                            {Array.isArray(activeStepData.stepRagContext) && activeStepData.stepRagContext.length > 0 && <CollapsibleList title="RAG Context (This Step)" items={activeStepData.stepRagContext} limit={5} initiallyExpanded={false} />}
                                            {activeStepData.stepLlmResponse && <details className="step-details"> <summary>LLM Analysis (JSON)</summary> <pre>{JSON.stringify(activeStepData.stepLlmResponse, null, 2)}</pre> </details>}
                                            {activeStepData.stepReasoning && <details className="step-details"> <summary>LLM Reasoning</summary> <pre style={{ whiteSpace: 'pre-wrap' }}>{activeStepData.stepReasoning}</pre> </details>}
                                        </div>
                                    );
                                })()
                            ) : ( displayDataHistory.length > 0 ? ( <div className="step-placeholder" style={{marginTop: '15px', fontStyle: 'italic', color: 'grey'}}> Select a step using the controls above the graph (Prev/Next/Play) to view chat details. </div> ) : null )}
                         </div>
                    )}
                </div> {/* End Left Column */}

                {/* Right Column: Graph */}
                <div className="right-column">
                   <div className="graph-container">
                         <div className="graph-header">
                              <span>Graph Visualization {activeStepIndex >= 0 ? `(Step ${activeStepIndex + 1} of ${displayDataHistory.length})` : '(No Step Selected)'}</span>
                              <div className="step-nav-buttons">
                                {displayDataHistory.length > 1 && ( <button onClick={handleToggleAutoPlayGraph} disabled={isLoading} className={`autoplay-button ${isAutoPlayingGraph ? 'active' : ''}`}> {isAutoPlayingGraph ? 'Pause' : 'Play'} </button> )}
                                <button onClick={handlePreviousStep} disabled={!canGoPrevious || isLoading || isAutoPlayingGraph}>Prev</button>
                                <button onClick={handleNextStep} disabled={!canGoNext || isLoading || isAutoPlayingGraph}>Next</button>
                              </div>
                         </div>
                        {/* ** Using the refined Graph Key ** */}
                        {/* The graph should render if activeGraphData has nodes/edges */}
                        {activeGraphData && activeGraphData.nodes && activeGraphData.nodes.length > 0 ? (
                            <Graph
                                key={`graph-step-${activeStepIndex}-${isViewingLog}-${uniqueId || selectedLogId}-${filterType}-${selectedDirectory}-${selectedCollection}`}
                                graph={activeGraphData}
                                options={graphOptions}
                                events={{ click: handleGraphClick }}
                            />
                        ) : ( <div className="graph-placeholder"> {isLoading ? "Loading graph..." : (displayDataHistory.length > 0 && activeStepIndex < 0) ? "Select a step or use Prev/Next/Play." : (displayDataHistory.length === 0) ? "Graph will appear here after starting query or loading chat." : "No graph data available for this step." } </div> )}
                    </div>
                </div> {/* End Right Column */}
            </div> {/* End Main Layout */}

             {/* Article/Statement Modal (No changes needed here) */}
             <Modal isOpen={isArticleModalOpen} onRequestClose={closeArticleModal} style={customModalStyles} contentLabel="Node Information">
                 {/* ... Modal Content JSX remains the same ... */ }
                 {isModalLoading && <div className="loading" style={{textAlign: 'center', padding: '20px'}}>Loading article details...</div>}
                 {!isModalLoading && modalContent ? (
                    // Article Details View
                    <div className="article-details-modal">
                        <h2>Article Details</h2>
                        {modalContent.error ? ( <p className="error">{modalContent.error}</p> ) : (
                            <>
                                <p><strong>ID:</strong> {modalContent.id}</p>
                                <p><strong>File Name:</strong> {modalContent.file_name || 'N/A'}</p>
                                <p><strong>Topic:</strong> {modalContent.topic_title || 'N/A'}</p>
                                {modalContent.title_summary && ( <details open><summary><strong>Summary</strong></summary><p>{modalContent.title_summary}</p></details> )}
                                {modalContent.key_concepts && ( <details><summary><strong>Key Concepts</strong></summary><pre>{modalContent.key_concepts}</pre></details> )}
                                {modalContent.text && ( <details><summary><strong>Full Text</strong></summary><pre className="markdown-text">{modalContent.text}</pre></details> )}
                            </>
                        )}
                        <div className="modal-button-group">
                            {selectedNodeInfoForModal && !modalContent?.error && ( <button onClick={handleModalBackToList} className="modal-cancel-button">Back to Statements</button> )}
                            <button onClick={closeArticleModal} className="modal-close-button">Close</button>
                         </div>
                    </div>
                ) : !isModalLoading && selectedNodeInfoForModal ? (
                    // Statement List View
                    <div className="statement-list-modal">
                        <h2>Statements for Node: "{selectedNodeInfoForModal.nodeLabel}"</h2>
                        {selectedNodeInfoForModal.connectedStatements.length > 0 ? (
                             <ul className="statement-list">
                                {selectedNodeInfoForModal.connectedStatements.map((item, index) => (
                                   <li key={index} className="statement-item">
                                        <span className="statement-text">{item.statement}</span>
                                        <button
                                            onClick={() => fetchArticleDetailsForModal(item.articleId)}
                                            className="view-details-button"
                                            title={`View details for Article ID: ${item.articleId}`}
                                            disabled={!item.articleId || item.articleId === 'N/A'}
                                        >
                                            View Details
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        ) : ( <p>No statements with associated Article IDs found connected to this node in the current view.</p> )}
                         <div className="modal-button-group"> <button onClick={closeArticleModal} className="modal-close-button">Close</button> </div>
                    </div>
                ) : (
                    <div style={{padding: '20px', textAlign: 'center'}}>
                        <p>Loading content...</p>
                         <div className="modal-button-group"> <button onClick={closeArticleModal} className="modal-close-button">Close</button> </div>
                    </div>
                 )}
            </Modal>

            {/* Config Modal (Passes updated props) */}
            <ConfigModal
                isOpen={isConfigModalOpen}
                onRequestClose={closeConfigModal}
                currentConfig={{ maxLoops, analyzeResponse, verifyCheckin, useRAG }}
                onSave={handleSaveConfig} // Prop from App.js
                currentFilterType={filterType} // Pass current selection state
                currentSelectedDirectory={selectedDirectory}
                currentSelectedCollection={selectedCollection}
                directories={directories}
                onSaveDataSource={handleSaveDataSource} // Local handler to update selection state
                isAuthenticated={isAuthenticated}
                handleLogout={handleLogout}
                setError={setError}
            />
        </>
    );
}

export default AppExplorer;